/**
 * Billing Service
 *
 * Provides read/write access to per-project billing configuration, including
 * the assigned invoice template, billing frequency, expense/purchase recharge
 * rules, NTE cap, and auto-generate settings.
 *
 * All functions call the backend API endpoints via /api/projects/{projectId}/billing-config.
 * No localStorage caching — the API is the sole source of truth.
 */

import { getApiBaseUrl } from '@/lib/data-service';
import { apiFetch } from '@/lib/api-error-handler';

// ============================================================================
// TYPES
// ============================================================================

export type BillingFrequency =
  | 'weekly'
  | 'fortnightly'
  | 'monthly'
  | 'quarterly'
  | 'on-completion'
  | 'milestone';

export type ExpenseRechargeRule = 'none' | 'at-cost' | 'marked-up';

export interface ProjectBillingConfig {
  projectId: string | number;

  /** ID of the invoice template assigned to this project. */
  invoiceTemplateId: string | number | null;

  /** How often invoices are generated for this project. */
  billingFrequency: BillingFrequency;

  /** Whether expenses are recharged to the client and at what rate. */
  expenseRechargeRule: ExpenseRechargeRule;

  /** Markup percentage applied when expenseRechargeRule is 'marked-up'. */
  expenseMarkupPercent?: number;

  /** Whether purchase requests are recharged to the client. */
  rechargesPurchases: boolean;

  /** Not-to-exceed cap in the project's billing currency (null = no cap). */
  nteCapAmount: number | null;

  /** Whether invoices are auto-generated on the billing frequency schedule. */
  autoGenerate: boolean;

  /**
   * Cost centre code passed to the finance system on Fixed Fee invoice posting.
   * Required for fixed-price projects — the finance posting API will fail without it.
   * Null for T&M projects (which carry cost centres from individual WIP entries).
   */
  defaultCostCentreCode: string | null;

  /**
   * Billing trigger type for fixed-price projects.
   * Determines when billing milestones are evaluated:
   * - 'date'             — each milestone fires on its dueDate
   * - 'percent-complete' — each milestone fires when completionPercent >= triggerPercent
   */
  billingTriggerType?: string;
}

// ============================================================================
// DEFAULTS
// ============================================================================

function defaultConfig(projectId: string | number): ProjectBillingConfig {
  return {
    projectId,
    invoiceTemplateId: null,
    billingFrequency: 'monthly',
    expenseRechargeRule: 'at-cost',
    expenseMarkupPercent: undefined,
    rechargesPurchases: true,
    nteCapAmount: null,
    autoGenerate: false,
    defaultCostCentreCode: null,
  };
}

// ============================================================================
// READ
// ============================================================================

/**
 * Returns the billing configuration for a given project.
 * Falls back to sensible defaults if no config has been saved yet.
 *
 * API: GET /api/projects/{projectId}/billing-config
 */
export async function getProjectBillingConfig(
  projectId: string | number,
): Promise<ProjectBillingConfig> {
  const data = await apiFetch<Record<string, unknown>>(`${getApiBaseUrl()}/projects/${projectId}/billing-config`);
  return { ...defaultConfig(projectId), ...data, projectId };
}

/**
 * Returns the ID of the invoice template assigned to a given project.
 * Returns null if no template has been assigned.
 *
 * API: GET /api/projects/{projectId}/billing-config
 */
export async function getProjectAssignedTemplate(
  projectId: string | number,
): Promise<string | number | null> {
  const config = await getProjectBillingConfig(projectId);
  return config.invoiceTemplateId;
}

// ============================================================================
// WRITE
// ============================================================================

/**
 * Saves updated billing configuration for a given project.
 * Merges the provided partial update with the existing config.
 *
 * API: PUT /api/projects/{projectId}/billing-config
 */
export async function updateProjectBillingConfig(
  projectId: string | number,
  updates: Partial<Omit<ProjectBillingConfig, 'projectId'>>,
): Promise<ProjectBillingConfig> {
  const current = await getProjectBillingConfig(projectId);
  const merged: ProjectBillingConfig = { ...current, ...updates, projectId };

  const data = await apiFetch<Record<string, unknown>>(`${getApiBaseUrl()}/projects/${projectId}/billing-config`, {
    method: 'PUT',
    body: JSON.stringify(merged),
  });
  return { ...defaultConfig(projectId), ...data, projectId };
}
